import React, { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSelectedCase } from "./utils/hooks";
import ScrollToTop from "../../components/utils/ScrollToTop";
import PageHeader from "../../components/PageHeader";
import Card from "../../components/DashboardCard";
import { describeTransactionType } from "../../utils/textUtils";
import styled from "styled-components/macro";
import { mediaQueries } from "../../styles/media";
import { Drafts } from "@styled-icons/material";
import { getFormattedDate } from "../../utils/textUtils";
import { sanitiseHtml } from "../../utils/sanitiseHtml";

export const useMarkNotificationRead = (selectedCase, notification) => {
  const sessionType = useSelector((state) => state.dashboardState.session.session_type);

  const markedNotificationIds = useRef(new Set());

  // Guard per notification id: prevent StrictMode duplicate request for one
  // notification while still allowing a new request when the route id changes.
  useEffect(() => {
    if (!notification) return;

    const markReadKey = `${selectedCase.id}:${notification.id}`;
    if (markedNotificationIds.current.has(markReadKey)) return;
    markedNotificationIds.current.add(markReadKey);

    const markRead = async () => {
      try {
        const resp = await fetch(`/api/dashboard/notification/${selectedCase.id}/${notification.id}`, {
          method: "PUT",
          credentials: "same-origin",
        });
        if (!resp.ok) {
          markedNotificationIds.current.delete(markReadKey);
          // Don't report client errors (4xx) to Sentry - these indicate expected
          // conditions like expired sessions, missing resources, or stale data.
          // Only report 5xx server errors that indicate actual bugs.
          if (resp.status >= 500) {
            const body = await resp.text();
            Sentry.captureException(new Error(`Non-success response: ${resp.status} ${resp.statusText}. ${body}`), {
              tags: { action: "MarkNotificationRead" },
            });
          }
        }
      } catch (err) {
        markedNotificationIds.current.delete(markReadKey);
        // Don't report transient network errors to Sentry - these are expected
        // on mobile devices with intermittent connectivity
        const isTransientNetworkError =
          err.message &&
          (err.message.includes("Load failed") ||
            err.message.includes("Failed to fetch") ||
            err.message.includes("NetworkError") ||
            err.message.includes("network"));
        if (!isTransientNetworkError) {
          console.error("Error marking notification as read", err);
          Sentry.captureException(err, { tags: { action: "MarkNotificationRead" } });
        }
      }
    };

    // Update client app db, set notification status to 'read'
    if (sessionType === "client" && notification?.status === "unread") {
      markRead();
    }
  }, [sessionType, selectedCase?.id, notification]);
};

const Notification = () => {
  const { notificationId } = useParams();

  const selectedCase = useSelectedCase({ caseScreen: "notification" });

  const notification = selectedCase?.notifications?.find((n) => n.id === notificationId);

  useMarkNotificationRead(selectedCase, notification);

  const createContent = () => {
    const notificationContent = notification.content;
    return { __html: sanitiseHtml(notificationContent) };
  };

  if (!notification) return null;

  let address = "";
  if (!!selectedCase.address) {
    address = "of " + selectedCase.address;
  }

  const cardTitle = `Your ${describeTransactionType(selectedCase.case_type)} ${address} (${selectedCase.case_reference})`;

  return (
    <React.Fragment>
      <ScrollToTop />
      <PageHeader>{cardTitle}</PageHeader>
      <Card title={null}>
        <StyledNotificationHeader>
          <StyledSmallScreenHeader>
            <StyledSmallScreenHeaderInner>
              <StyledNotificationIcon>
                <Drafts size="20" />
              </StyledNotificationIcon>
              {getFormattedDate(notification.created_at)}
            </StyledSmallScreenHeaderInner>

            <h2>{notification.subject}</h2>
          </StyledSmallScreenHeader>

          <StyledLargeScreenHeader>
            <StyledNotificationIcon>
              <Drafts size="24" />
            </StyledNotificationIcon>

            <h2>{notification.subject}</h2>

            <StyledNotificationDate>{getFormattedDate(notification.created_at)}</StyledNotificationDate>
          </StyledLargeScreenHeader>
        </StyledNotificationHeader>

        <StyledNotificationContent dangerouslySetInnerHTML={createContent()} />
      </Card>
    </React.Fragment>
  );
};

export default Notification;

const StyledSmallScreenHeader = styled.div`
  ${mediaQueries.md} {
    display: none;
  }
`;

const StyledSmallScreenHeaderInner = styled.div`
  display: flex;
  align-items: center;

  margin-top: 16px;
  margin-bottom: 8px;
`;

const StyledLargeScreenHeader = styled.div`
  display: none;

  ${mediaQueries.md} {
    display: flex;
    align-items: center;
  }
`;

const StyledNotificationHeader = styled.div`
  padding: 0 0 10px 0;
  margin-bottom: 1.5em;

  color: var(--juno-color-text);
  font-weight: var(--juno-font-weight-bold);
  border-bottom: 1px solid ${(props) => (props.read ? "var(--juno-color-white)" : "var(--juno-color-border)")};

  h2 {
    margin: 0;
    font-size: 18px;
    ${mediaQueries.md} {
      font-size: 20px;
      max-width: 75%;
    }
    line-height: 1.25;
  }
`;

export const StyledNotificationIcon = styled.i`
  flex-shrink: 0;
  color: ${(props) => (props.read ? "#1e9952" : "inherit")};

  /* stylelint-disable no-descending-specificity */
  padding-right: 8px;
  ${mediaQueries.md} {
    padding-right: 16px;
  }
  /* stylelint-enable no-descending-specificity */
`;

const StyledNotificationDate = styled.div`
  margin-left: auto;
  text-align: right;
`;

const StyledNotificationContent = styled.div`
  ul {
    font-size: var(--juno-font-size-body-sm);
    line-height: 1.5;
    margin-bottom: 1em;
    ${mediaQueries.md} {
      font-size: var(--juno-font-size-body-md);
    }
  }

  ${mediaQueries.md} {
    width: 75%;
  }
`;
